import React, { useState, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudUploadAlt, faFileAlt, faTimes, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useIntl } from "react-intl";


const appleEntrance = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;



const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(235, 235, 245, 0.3);
  backdrop-filter: blur(10px) saturate(180%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  width: 100%;
  max-width: 520px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #E5E5E7;
  border-radius: 8px; 
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  position: relative;
  animation: ${appleEntrance} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    opacity: 0.02;
    pointer-events: none;
  }
`;

const Header = styled.header`
  padding: 20px 24px;
  border-bottom: 1px solid #F2F2F7;
  
  h2 {
    color: #1D1D1F;
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    letter-spacing: -0.01em;
  }
`;

const Content = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MaxSizeLabel = styled.span`
  color: #86868B;
  font-size: 12px;
  font-weight: 400;
`;

const DropZone = styled.div`
  border: 1px dashed ${props => props.isDragging ? '#FF8F40' : '#D1D1D6'};
  background: ${props => props.isDragging ? 'rgba(255, 143, 64, 0.05)' : '#F5F5F7'};
  border-radius: 8px;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: #FF8F40;
    background: #FBFBFC;
  }

  svg {
    font-size: 32px;
    color: ${props => props.isDragging ? '#FF8F40' : '#C7C7CC'};
    transition: color 0.2s;
  }

  p {
    margin: 0;
    font-size: 14px;
    color: #1D1D1F;
    text-align: center;
    line-height: 1.4;
    max-width: 250px;
  }
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 180px;
  overflow-y: auto;
  margin-top: 8px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #D1D1D6; border-radius: 10px; }
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: #FFFFFF;
  border: 1px solid #F2F2F7;
  border-radius: 8px;
  animation: ${appleEntrance} 0.3s ease;

  .file-icon { color: #86868B; font-size: 14px; }
  .file-name { color: #1D1D1F; font-size: 13px; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #C7C7CC;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover { color: #FF3B30; background: rgba(255, 59, 48, 0.05); }
`;

const Footer = styled.footer`
  padding: 16px 24px;
  background: #F5F5F7;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const SecondaryButton = styled.button`
  height: 40px;
  padding: 0 20px;
  background: transparent;
  border: 1px solid #D1D1D6;
  border-radius: 8px;
  color: #1D1D1F;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover { background: rgba(0, 0, 0, 0.02); border-color: #86868B; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PrimaryButton = styled.button`
  height: 40px;
  padding: 0 24px;
  background: #1D1D1F;
  border: none;
  border-radius: 8px;
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { background: #E5E5E7; color: #A1A1A6; cursor: not-allowed; transform: none; }

  .spinner { animation: ${rotate} 1s linear infinite; }
`;

const HiddenInput = styled.input`
  display: none;
`;



const FileUploadModal = ({
  isOpen,
  onClose,
  onFileUpload,
  multiple = true,
  clientId = null,
}) => {
  const intl = useIntl();
  const [fileList, setFileList] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleFileSelection = (files) => {
    const newFiles = Array.from(files);
    if (multiple) {
      setFileList((prev) => [...prev, ...newFiles]);
    } else {
      setFileList(newFiles);
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => {
    setIsDragging(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelection(e.dataTransfer.files);
    }
  };

  const handleRemoveFile = (index) => {
    setFileList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleConfirmUpload = async () => {
    if (fileList.length > 0 && onFileUpload) {
      setIsUploading(true);
      try {
        clientId
          ? await onFileUpload(clientId, fileList)
          : await onFileUpload(fileList);
        setFileList([]);
        onClose();
      } catch (error) {
        console.error("Upload failed", error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  return (
    <Overlay onClick={isUploading ? null : onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <Header>
          <h2>
            {intl.formatMessage({
              id: "fileUploadModal.title",
              defaultMessage: "Upload de Arquivos",
            })}
          </h2>
        </Header>

        <Content>
          <MaxSizeLabel>
            {intl.formatMessage({
              id: "fileUploadModal.maxSize",
              defaultMessage: "Tamanho máximo por arquivo: 50MB",
            })}
          </MaxSizeLabel>

          <HiddenInput
            type="file"
            ref={fileInputRef}
            multiple={multiple}
            onChange={(e) => handleFileSelection(e.target.files)}
          />

          <DropZone
            isDragging={isDragging}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <FontAwesomeIcon icon={faCloudUploadAlt} />
            <p>
              {intl.formatMessage({
                id: "fileUploadModal.dragAreaText",
                defaultMessage: "Clique ou arraste arquivos para esta área para fazer upload",
              })}
            </p>
          </DropZone>

          {fileList.length > 0 && (
            <FileList>
              {fileList.map((file, index) => (
                <FileItem key={`${file.name}-${index}`}>
                  <FontAwesomeIcon icon={faFileAlt} className="file-icon" />
                  <span className="file-name">{file.name}</span>
                  <RemoveButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile(index);
                    }}
                    disabled={isUploading}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </RemoveButton>
                </FileItem>
              ))}
            </FileList>
          )}
        </Content>

        <Footer>
          <SecondaryButton onClick={onClose} disabled={isUploading}>
            {intl.formatMessage({
              id: "fileUploadModal.cancel",
              defaultMessage: "Cancelar",
            })}
          </SecondaryButton>

          <PrimaryButton
            onClick={handleConfirmUpload}
            disabled={fileList.length === 0 || isUploading}
          >
            {isUploading && <FontAwesomeIcon icon={faSpinner} className="spinner" />}
            {intl.formatMessage({
              id: "fileUploadModal.confirmUpload",
              defaultMessage: "Confirmar Upload",
            })}
          </PrimaryButton>
        </Footer>
      </ModalContainer>
    </Overlay>
  );
};

export default FileUploadModal;